/**
 * Phase 8 · C-49 · Shared viz — ChannelMixBar.
 *
 * Stacked horizontal bar + legend. Segment colours are deterministic per
 * known channel (internal / linkedin / naukri / peerlist / other) with a
 * hash-based fallback for anything else.
 */
import './tokens.css';

export interface ChannelMixBarProps {
  segments: Record<string, number>;
  height?: number;
  /** Include a small chip legend below the bar (default true). */
  legend?: boolean;
}

const CHANNEL_COLOR: Record<string, string> = {
  internal: 'var(--viz-success)',
  linkedin: '#0A66C2',
  naukri:   '#2B67F6',
  peerlist: '#8B80F9',
  other:    '#94A3B8',
};

function colorFor(key: string): string {
  const normalized = key.toLowerCase();
  if (CHANNEL_COLOR[normalized]) return CHANNEL_COLOR[normalized];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 55% 55%)`;
}

export function ChannelMixBar({ segments, height = 10, legend = true }: ChannelMixBarProps) {
  const entries = Object.entries(segments).filter(([, v]) => v > 0);
  const total = entries.reduce((a, [, v]) => a + v, 0);

  if (total === 0) {
    return (
      <p className="text-[11px]" style={{ color: 'var(--viz-text-muted)' }}>
        No sourcing data yet.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        className="flex w-full overflow-hidden rounded-full"
        style={{ height, background: 'var(--viz-track)' }}
        role="img"
        aria-label={`Channel mix: ${entries.map(([k, v]) => `${k} ${v}`).join(', ')}`}
      >
        {entries.map(([k, v]) => (
          <div
            key={k}
            style={{ width: `${(v / total) * 100}%`, background: colorFor(k) }}
            title={`${k}: ${v}`}
          />
        ))}
      </div>
      {legend && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]" style={{ color: 'var(--viz-text-muted)' }}>
          {entries.map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5 capitalize">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: colorFor(k) }} aria-hidden />
              {k} <span className="tabular-nums" style={{ color: 'var(--viz-text)' }}>{v}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
